import dayjs from 'dayjs'
import uniqBy from 'lodash.uniqby'
import { defineStore } from 'pinia'
import type { ISbResult } from 'storyblok-js-client'

import { useUser } from '@/store/modules/App/user'
import { client } from '@/services/client'

import { USER_NOTIFICATION_PREFIX } from '@/components/ContentManager/WhatsNewModal/constants'
import type { NewsWrapper } from '@/components/ContentManager/WhatsNewModal/components/WhatsNewModalNewsItem/types'
import {
  getWhatsNewPayload,
  WHATS_NEW_ENDPOINT,
} from '@base/BaseSidebar/components/SidebarWhatsNewShortcut'

export type State = {
  appNews: NewsWrapper[]
  appNewsTotalCount: number
  whatsNewModalLastViewDate: Date
  openedAt: Date
}

const defaultState = (): State => ({
  appNews: [] as NewsWrapper[],
  appNewsTotalCount: null as unknown as number,
  whatsNewModalLastViewDate: null as unknown as Date,
  openedAt: new Date(),
})

export const useApplication = defineStore('application', {
  state: defaultState,
  getters: {
    getNews: (state: State): NewsWrapper[] => state.appNews,
    getAppNewsTotalCount: (state: State): number => state.appNewsTotalCount,
    getWhatsNewModalLastViewDate: (state: State): Date =>
      state.whatsNewModalLastViewDate,
    getUnreadNewsCount: (state: State): number | undefined => {
      if (!state.whatsNewModalLastViewDate) {
        return state.appNews?.length
      }
      return state.appNews?.filter((newsWrapper: NewsWrapper) => {
        return dayjs(newsWrapper.first_published_at).isAfter(
          dayjs(state.whatsNewModalLastViewDate),
        )
      }).length
    },
  },
  actions: {
    setAppNewsTotalCount(totalCount: number): void {
      this.appNewsTotalCount = totalCount
    },

    setterWhatsNewModalLastViewDate(date: Date): Date {
      return (this.whatsNewModalLastViewDate = date)
    },

    async getAppNews(page = 1): Promise<unknown> {
      const response: ISbResult = await client.get(WHATS_NEW_ENDPOINT, {
        params: getWhatsNewPayload(page),
      })

      const responseHeaders = response.headers as Headers & { total: string }

      this.setAppNewsTotalCount(parseInt(responseHeaders.total))

      const appNews = response.data.stories ?? []
      this.appNews = uniqBy([...this.appNews, ...appNews], 'id')

      return response?.data
    },

    checkWhatsNewModalLastViewDate(): void {
      const userStore = useUser()
      const [lastVisitDateNotification] = userStore.findUserNotifiedValues(
        USER_NOTIFICATION_PREFIX,
      )

      if (lastVisitDateNotification) {
        const lastVisitDateString = lastVisitDateNotification
          .split(USER_NOTIFICATION_PREFIX)
          .pop()

        const lastVisitDate = lastVisitDateString
          ? new Date(lastVisitDateString)
          : new Date()
        this.setterWhatsNewModalLastViewDate(lastVisitDate)
      }
    },

    async setWhatsNewModalLastViewDate(): Promise<void> {
      const userStore = useUser()
      const [lastVisitDateNotification] = userStore.findUserNotifiedValues(
        USER_NOTIFICATION_PREFIX,
      )

      await userStore.removeUserNotifiedValue(lastVisitDateNotification)

      const lastVisitDate = new Date()

      const lastVisitDateNotificationValue = dayjs(lastVisitDate).format(
        `[${USER_NOTIFICATION_PREFIX}]YYYY-MM-DDTHH:mm:ss`,
      )

      await userStore.addUserNotifiedValue(lastVisitDateNotificationValue)

      this.setterWhatsNewModalLastViewDate(lastVisitDate)
    },

    setTitle(title: string): void {
      window.document.title = title
        ? `${title.trim()} - Storyblok`
        : 'Storyblok'
    },
  },
})
